import React, { PropTypes } from 'react';
import { withRouter } from 'react-router';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';

import BcReactComponent from '@bibliocommons/base-bc-react-component';
import { holdItemShape, borrowingQuotaShape, branchShape } from '@bibliocommons/bc-prop-types';
import Pagination from '@bibliocommons/pagination';
import ProgressBar from '@bibliocommons/shared-progress-bar';

import HoldsToReviewContainer from '../../../../containers/HoldsToReviewContainer';

import BorrowingList from '../../shared/BorrowingList';
import HoldsList from '../HoldsList';
import HoldsUtilityBar from '../HoldsUtilityBar';
import HoldsEmptyContentPanel from '../HoldsEmptyContentPanel';
import HoldsBatchActions from '../HoldsBatchActions';
import BorrowingHeader from '../../shared/BorrowingHeader';
import { GLOBAL_MESSAGING_ID } from '../../../../constants/MessagingConstants';

import './Holds.scss';

export class Holds extends BcReactComponent {
  constructor(props) {
    super(props);
    this.bindAll(
      'handleSort',
      'handleBatchCancel',
      'handleBatchAddToShelf',
      'handleBatchResumeHolds');
  }

  componentDidMount() {
    this.redirectIfSingleStatus();
  }

  componentDidUpdate(prevProps) {
    const { items, router, selectedStatus, presentStatuses } = this.props;

    // Unselect status if there are no longer any holds with status
    if (selectedStatus && items.size === 0) {
      router.replace('/v2/holds');
    }

    if (!prevProps.presentStatuses.equals(presentStatuses)) {
      this.redirectIfSingleStatus();
    }
  }

  redirectIfSingleStatus() {
    const { router, presentStatuses, selectedStatus } = this.props;

    // Redirect to the route for a specific status if there is only one status
    if (!selectedStatus && presentStatuses && presentStatuses.size === 1) {
      router.replace(`/v2/holds/${presentStatuses.first().toLowerCase()}`);
    }
  }


  allItemsDigital() {
    return this.props.items.every(
      (item) => item.getIn(['bib', 'policy', 'materialType']) === 'DIGITAL'
    );
  }

  getPrintURL() {
    const { search } = this.props.location;
    return `/v2/print/holds${search}`;
  }

  hasReadyForPickupItems() {
    return this.props.items
    .map(item => item.get('status'))
    .toSet()
    .toList()
    .includes('READY_FOR_PICKUP');
  }

  getSortOptions() {
    const currentSort = this.props.router.location.query.sort || 'status';
    let sortOptions = [
      'status',
      'holdsPosition',
      'expiryDate',
      'title',
      'author',
      'format'
    ];

    sortOptions = this.hasReadyForPickupItems() ? sortOptions.concat(['pickupByDate']) : sortOptions;

    return sortOptions.reduce(
      (acc, val) =>
        acc.set(
          val,
          Immutable.Map({
            id: val,
            applied: val === currentSort
          })
        ),
      Immutable.Map()
    ).toList();
  }


  handleSort(sortType) {
    const { location, router } = this.props;
    router.push({
      ...location,
      query: {
        ...location.query,
        sort: sortType
      }
    });
  }

  handleBatchCancel({ shelfState, items }) {
    const { accountId } = this.props;

    this.props.holdActions.cancelHold({
      accountId,
      metadataIds: items
        .map(item => item.get('metadataId'))
        .filter(metadataId => !this.props.shelves.has(metadataId))
        .toList()
        .toJS(),
      holdIds: items.map(item => item.get('id')).toList().toJS(),
      shelfState,
      messagingId: GLOBAL_MESSAGING_ID,
      isBatch: true
    });
  }

  handleBatchAddToShelf(items, shelfName) {
    const metadataIds = items
      .map(item => item.get('metadataId'))
      .toSet()
      .toList();

    this.props.shelfActions.batchAddToShelf({ metadataIds, shelfName });
  }

  handleBatchResumeHolds(items) {
    const { accountId, holdActions } = this.props;
    const holdIds = items
      .map(item => item.get('id'))
      .toSet()
      .toList()
      .toJS();

    holdActions.pauseOrResumeHolds({
      accountId,
      holdIds,
      suspended: { status: false }
    });
  }

  renderBatchActionButtons(itemsForAction) {
    return (
      <HoldsBatchActions
        itemsForAction={itemsForAction}
        handleBatchCancel={this.handleBatchCancel}
        handleBatchAddToShelf={this.handleBatchAddToShelf} />
    );
  }

  renderBorrowingList() {
    const { items, showStaffPicksLink, branches, pagination, fetching } = this.props;

    const actionList = items.map(item => Immutable.Map({
      id: item.get('holdsId'),
      metadataId: item.get('metadataId'),
      actions: item.get('actions')
    }));

    if (!items || items.size === 0) {
      return <HoldsEmptyContentPanel fetching={fetching} showStaffPicksLink={showStaffPicksLink} />;
    }

    return (
      <BorrowingList>
        <HoldsUtilityBar
          allItemsDigital={this.allItemsDigital()}
          status={this.props.selectedStatus}
          totalCount={this.props.totalUnfilteredCount}
          materialTypeCounts={this.props.materialTypeCounts}
          sortOptions={this.getSortOptions()}
          printURL={this.getPrintURL()}
          quotas={this.props.quotas}
          pagination={this.props.pagination}
          handleSort={this.handleSort} />
        <HoldsList
          accountId={this.props.accountId}
          items={items}
          totalCount={items.size}
          branches={branches}
          actionList={actionList}
          shelfTransactions={this.props.shelfTransactions}
          workflowActions={this.props.workflowActions}
          holdActions={this.props.holdActions}
          availabilityDetailActions={this.props.availabilityDetailActions}
          handleBatchAddToShelf={this.handleBatchAddToShelf}
          handleBatchCancel={this.handleBatchCancel}
          handleBatchResumeHolds={this.handleBatchResumeHolds}
          renderBatchActionButtons={(itemsForAction) => this.renderBatchActionButtons(itemsForAction)}
          holdsTransactions={this.props.holdsTransactions} />
        <section className="bottom-controls">
          <Pagination
            showPages
            isFetching={fetching}
            page={pagination.get('page')}
            limit={pagination.get('limit')}
            count={pagination.get('count')}
            pages={pagination.get('pages')} />
        </section>
      </BorrowingList>
    );
  }

  render() {
    return (
      <div className="cp-holds">
        <ProgressBar active={this.props.fetching} />
        <BorrowingHeader
          headingKey="section_title_holds"
          taglineKey="section_tagline_holds"
          helpLinkPage="005borrowing" />
        {this.renderBorrowingList()}
        <HoldsToReviewContainer />
      </div>
    );
  }
}

Holds.propTypes = {
  items: ImmutablePropTypes.listOf(holdItemShape),
  selectedStatus: PropTypes.string,
  presentStatuses: ImmutablePropTypes.listOf(PropTypes.string),
  totalUnfilteredCount: PropTypes.number,
  borrowingActions: PropTypes.objectOf(PropTypes.func),
  materialTypeCounts: ImmutablePropTypes.mapContains({
    PHYSICAL: PropTypes.number,
    DIGITAL: PropTypes.number
  }),
  printURL: PropTypes.string,
  quotas: ImmutablePropTypes.listOf(borrowingQuotaShape),
  showStaffPicksLink: PropTypes.bool,
  branches: ImmutablePropTypes.listOf(branchShape).isRequired,
  accountId: PropTypes.number,
  holdsTransactions: ImmutablePropTypes.map,
  shelves: ImmutablePropTypes.map,
  shelfTransactions: ImmutablePropTypes.map,
  fetching: PropTypes.bool
};

export default withRouter(Holds);



// WEBPACK FOOTER //
// ./app/components/borrowing/holds/Holds/Holds.jsx